import { estimateTamUsd, formatUsdCompact } from '@/components/value-chain/revenueMap';
import type { TreeIndex } from '@/components/value-chain/treeIndex';
import type { ValueChainGraph } from '@/types/valueChain';

export type ProductGlyph =
  | 'wifi'
  | 'rocket'
  | 'satellite'
  | 'astronaut'
  | 'flask'
  | 'ellipsis'
  | 'home'
  | 'buildings'
  | 'package'
  | 'briefcase'
  | 'shield'
  | 'gear'
  | 'globe'
  | 'nasa';

export type ProductLine = {
  id: string;
  label: string;
  revenue: number;
  sharePct: number;
  growthPct: number;
  color: string;
  accent: string;
  glyph: ProductGlyph;
  graphMatch?: string[];
  children?: ProductLine[];
};

export type ProductRevenueModel = {
  ticker: string;
  company: string;
  periodLabel: string;
  asOfLabel: string;
  totalRevenue: number;
  totalGrowthPct: number;
  grossMarginPct: number | null;
  employees: number | null;
  headquarters: string | null;
  layout: 'spacex' | 'treemap';
  lines: ProductLine[];
  takeaways: string[];
};

const SPCX_TOTAL = 26_800_000_000;

function line(
  partial: Omit<ProductLine, 'sharePct'> & { sharePct?: number },
  total = SPCX_TOTAL,
): ProductLine {
  const sharePct = partial.sharePct ?? Math.round((partial.revenue / total) * 100);
  return {
    ...partial,
    sharePct,
    children: partial.children?.map((child) => line(child, total)),
  };
}

const SPCX_LINES: ProductLine[] = [
  line({
    id: 'starlink',
    label: 'Starlink',
    revenue: 12_900_000_000,
    sharePct: 48,
    growthPct: 44,
    color: '#3b9aef',
    accent: '#1d6fd1',
    glyph: 'wifi',
    graphMatch: ['starlink'],
    children: [
      {
        id: 'starlink-consumer',
        label: 'Consumer',
        revenue: 7_400_000_000,
        sharePct: 28,
        growthPct: 44,
        color: '#2f7fe4',
        accent: '#1d6fd1',
        glyph: 'home',
        graphMatch: ['starlink'],
      },
      {
        id: 'starlink-enterprise',
        label: 'Enterprise & Government',
        revenue: 5_200_000_000,
        sharePct: 19,
        growthPct: 38,
        color: '#3b8eec',
        accent: '#1d6fd1',
        glyph: 'buildings',
        graphMatch: ['starlink'],
      },
      {
        id: 'starlink-kits',
        label: 'Connectivity Products (Kits)',
        revenue: 300_000_000,
        sharePct: 1,
        growthPct: 12,
        color: '#1f6fd0',
        accent: '#1557a8',
        glyph: 'package',
        graphMatch: ['starlink', 'kit'],
      },
    ],
  }),
  line({
    id: 'launch',
    label: 'Launch Services',
    revenue: 8_600_000_000,
    sharePct: 32,
    growthPct: 18,
    color: '#f06a3d',
    accent: '#d9482b',
    glyph: 'rocket',
    graphMatch: ['launch', 'falcon'],
    children: [
      {
        id: 'launch-commercial',
        label: 'Commercial',
        revenue: 5_100_000_000,
        sharePct: 18,
        growthPct: 16,
        color: '#ef7a4a',
        accent: '#d9482b',
        glyph: 'briefcase',
        graphMatch: ['launch', 'falcon'],
      },
      {
        id: 'launch-government',
        label: 'Government',
        revenue: 3_500_000_000,
        sharePct: 13,
        growthPct: 21,
        color: '#e35332',
        accent: '#c43a22',
        glyph: 'shield',
        graphMatch: ['launch', 'falcon'],
      },
    ],
  }),
  line({
    id: 'space-systems',
    label: 'Space Systems & Other',
    revenue: 3_500_000_000,
    sharePct: 13,
    growthPct: 31,
    color: '#3ccf7a',
    accent: '#16a34a',
    glyph: 'satellite',
    graphMatch: ['satellite', 'starshield'],
    children: [
      {
        id: 'space-satellites',
        label: 'Satellites',
        revenue: 2_100_000_000,
        sharePct: 8,
        growthPct: 34,
        color: '#2bb86a',
        accent: '#15803d',
        glyph: 'satellite',
        graphMatch: ['satellite', 'starshield'],
      },
      {
        id: 'space-other',
        label: 'Other Space Systems',
        revenue: 1_400_000_000,
        sharePct: 5,
        growthPct: 26,
        color: '#22a85f',
        accent: '#166534',
        glyph: 'gear',
        graphMatch: ['satellite'],
      },
    ],
  }),
  line({
    id: 'human-spaceflight',
    label: 'Human Spaceflight',
    revenue: 1_900_000_000,
    sharePct: 7,
    growthPct: 7,
    color: '#9b6bdb',
    accent: '#7c3aed',
    glyph: 'astronaut',
    graphMatch: ['dragon', 'crew'],
    children: [
      {
        id: 'human-nasa',
        label: 'NASA (Commercial Crew)',
        revenue: 1_600_000_000,
        sharePct: 6,
        growthPct: 6,
        color: '#7c4ec4',
        accent: '#6d28d9',
        glyph: 'nasa',
        graphMatch: ['dragon', 'crew'],
      },
      {
        id: 'human-other',
        label: 'Other',
        revenue: 300_000_000,
        sharePct: 1,
        growthPct: 14,
        color: '#b07ee8',
        accent: '#7c3aed',
        glyph: 'globe',
        graphMatch: ['dragon'],
      },
    ],
  }),
  line({
    id: 'starship',
    label: 'Starship & Development',
    revenue: 800_000_000,
    sharePct: 3,
    growthPct: 62,
    color: '#e8b84a',
    accent: '#ca8a04',
    glyph: 'flask',
    graphMatch: ['starship'],
  }),
  line({
    id: 'other',
    label: 'Other / Non-core',
    revenue: 500_000_000,
    sharePct: 2,
    growthPct: -12,
    color: '#8b929c',
    accent: '#64748b',
    glyph: 'ellipsis',
  }),
];

const SPCX_MODEL: ProductRevenueModel = {
  ticker: 'SPCX',
  company: 'SpaceX',
  periodLabel: 'TTM (Jun 2026)',
  asOfLabel: 'Sep 13, 2026',
  totalRevenue: SPCX_TOTAL,
  totalGrowthPct: 29,
  grossMarginPct: 55,
  employees: 13_000,
  headquarters: 'Hawthorne, CA',
  layout: 'spacex',
  lines: SPCX_LINES,
  takeaways: [
    'Starlink is the largest revenue stream at ~48%, driven by rapid subscriber growth.',
    'Launch services remain a major and stable revenue source at ~32%.',
    'Space systems (satellites, other) is growing, supported by Starlink and future constellations.',
    'Human spaceflight (Crew Dragon) provides recurring government revenue.',
    'Starship & development is a smaller revenue contributor today but potentially the largest long-term opportunity.',
  ],
};

export function isSpcxGraph(graph: ValueChainGraph): boolean {
  const hay = `${graph.ticker ?? ''} ${graph.name} ${graph.id} ${graph.product_slug ?? ''}`.toLowerCase();
  return hay.includes('spcx') || hay.includes('spacex') || hay.includes('starlink');
}

export function resolveProductRevenue(
  graph: ValueChainGraph,
  graphs: ValueChainGraph[],
  index: TreeIndex,
): ProductRevenueModel {
  if (isSpcxGraph(graph) || graphs.some(isSpcxGraph)) return SPCX_MODEL;

  const products = uniqueGraphs(graphs.length ? graphs : [graph]);
  const tam = estimateTamUsd(index);
  const each = products.length ? tam / products.length : tam;
  const total = each * Math.max(1, products.length);
  const lines: ProductLine[] = products.map((item, i) => {
    const palette = GENERIC_PALETTE[i % GENERIC_PALETTE.length];
    return {
      id: item.id,
      label: item.name,
      revenue: each,
      sharePct: Math.round((each / total) * 100) || 100,
      growthPct: 8 - (i % 5) * 3,
      color: palette.color,
      accent: palette.accent,
      glyph: GENERIC_GLYPHS[i % GENERIC_GLYPHS.length],
      graphMatch: [item.product_slug || item.id, item.name],
    };
  });

  const company =
    graph.ticker === 'SPCX'
      ? 'SpaceX'
      : graph.ticker ||
        graph.nodes.find((node) => node.company)?.company ||
        'Company';

  return {
    ticker: graph.ticker || '—',
    company,
    periodLabel: 'Estimated annual',
    asOfLabel: new Date().toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }),
    totalRevenue: total,
    totalGrowthPct: 12,
    grossMarginPct: null,
    employees: null,
    headquarters: null,
    layout: 'treemap',
    lines,
    takeaways: deriveGenericTakeaways(lines, total),
  };
}

export function matchProductGraph(
  graphs: ValueChainGraph[],
  line: ProductLine,
): ValueChainGraph | null {
  const needles = (line.graphMatch ?? [line.label]).map((value) => value.toLowerCase());
  return (
    graphs.find((graph) => {
      const hay = `${graph.name} ${graph.id} ${graph.product_slug ?? ''}`.toLowerCase();
      return needles.some((needle) => hay.includes(needle));
    }) ?? null
  );
}

export function formatUsdMap(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 100_000_000) return `$${(value / 1_000_000_000).toFixed(1)}B`;
  return formatUsdCompact(value);
}

const GENERIC_PALETTE = [
  { color: '#3b9aef', accent: '#1d6fd1' },
  { color: '#f06a3d', accent: '#d9482b' },
  { color: '#3ccf7a', accent: '#16a34a' },
  { color: '#9b6bdb', accent: '#7c3aed' },
  { color: '#e8b84a', accent: '#ca8a04' },
  { color: '#8b929c', accent: '#64748b' },
];

const GENERIC_GLYPHS: ProductGlyph[] = ['package', 'wifi', 'rocket', 'satellite', 'gear', 'globe'];

function uniqueGraphs(graphs: ValueChainGraph[]): ValueChainGraph[] {
  const seen = new Set<string>();
  return graphs.filter((graph) => {
    const key = (graph.product_slug || graph.id).toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function deriveGenericTakeaways(lines: ProductLine[], total: number): string[] {
  const top = lines[0];
  const insights = [
    top
      ? `${top.label} is the largest mapped product line at ${top.sharePct || Math.round((top.revenue / total) * 100)}% of estimated revenue.`
      : 'Product revenue is estimated from the mapped value-chain graphs.',
  ];
  if (lines[1]) insights.push(`${lines[1].label} is the next-largest contributor.`);
  insights.push('Click a tile to open the supplier revenue map for that product.');
  return insights.slice(0, 5);
}
